import { h, Component } from 'preact';
import { applyPatch, Operation } from 'fast-json-patch';
import $ from "../utils/net";
import OriginalConfig from './config';
import { makeReactive } from '../utils/reactive-store';
import PrematchFeedService from './prematch-feed';

const Config = OriginalConfig as any as {
	softionAPI: string;
	softionLiveAPI: string;
	publickey: string;
	lang: string;
	minLiveOdd: number;
	allowedLiveSports: Record<string, boolean>;
};

interface Tab {
	id: number;
	name: string;
}

interface LiveOddNode {
	id: string;
	name: string;
	value: number;
	active: boolean;
	live: boolean;
	info: any;
}

interface LiveMarketNode {
	id: string;
	name: string;
	label: string;
	active: boolean;
	order: number;
	legatura: string;
	categories: Tab[];
	sub: Record<string, LiveOddNode>;
}

interface LiveEventNode {
	parent: string;
	id: string;
	home: string;
	away: string;
	sub: Record<string, LiveMarketNode>;
	brId: number | string;
	markets: any;
	provider: string;
	sport: string;
	sportlabel: string;
	start: Date;
	active: boolean;
	result: string;
	tracker: any;
	misc: any;
	current: {
		info: any;
		result: string[];
		gameresult: any;
	};
	status: {
		timer: string | false;
		running: boolean;
		phase: string;
	};
	teams: {
		home: { id: string; name: string; uid: string | number };
		away: { id: string | null; name: string | null; uid: string | number | null };
	};
}

interface LiveTournamentNode {
	parent: string;
	id: string;
	name: string;
	label: string;
	order: number;
	sub: Record<string, LiveEventNode>;
	elite?: boolean;
	category: {name: string, label: string, order: number}
}

interface LiveSportNode {
	id: string;
	name: string;
	label: string;
	count: number;
	order: number;
	sub: Record<string, LiveTournamentNode>;
}

const once = (fn: Function, n = 1) => () => n-- > 0 ? fn() : null;
const groupBy = (tag: string) => (c: Record<string, any>, data: any) => ({ ...c, [data[tag]]: data });
const indexMarkets = (odds: any[]) => Object.values(odds || []).reduce(groupBy('id'), {});

const HARD_TRANSLATIONS: Record<string, (name: string) => string> = {
	"es": name => name.replace(/W1|Team 1/g, "Local").replace(/W2|Team 2/g, "Visitante")
};
const hardTranslate = (name: string, lang = Config.lang) => HARD_TRANSLATIONS[lang] ? HARD_TRANSLATIONS[lang](name) : name;

const normalizeMarket = (E: any, M: any): LiveMarketNode => ({
	id: '/market/' + E.id + '/' + M.id,
	name: hardTranslate(M.name),
	label: M.label,
	active: M.active,
	order: M.order,
	legatura: ((M.sub[0] || {}).legatura || [1, 30]).join('-'),
	categories: (M.categories || [{ id: 0, name: 'All' }]).concat(marketCategories(M)),
	sub: (M.sub as any[]).map(O => ({
		id: '/odd/' + E.id + '/' + M.id + '/' + O.id,
		name: hardTranslate(O.name),
		value: O.value,
		active: O.active && O.value >= Config.minLiveOdd,
		live: true,
		info: O.info
	})).reduce(groupBy('id'), {})
})

const normalizeEvent = (S: any, T: any, E: any): LiveEventNode => ({
	parent: '/tournament/' + S.id + '/' + T.id,
	id: '/event/' + E.id,
	home: (E.teams.home || {}).name,
	away: (E.teams.away || {}).name,
	sub: Object.values(E._odds || []).map((M: any) => normalizeMarket(E, M)).reduce(groupBy('id'), {}),
	// Da qui in poi non presente nel prematch
	brId: E.brId,
	markets: E.markets,
	provider: E.provider,
	sport: '/sport/' + E.sport,
	sportlabel: S.label,
	start: new Date(E.start),
	active: true,
	result: E.result,
	tracker: E.tracker,
	misc: E.misc,
	current: {
		info: E.current.info,//["A", ""],
		result: E.current.result.split('-'),
		gameresult: E.current['game-result']
	},
	status: {
		timer: E.status.timer == '0:0' ? false : E.status.timer,
		running: E.status.running,
		phase: E.status.phase
	},
	teams: {
		home: {
			id: '/team/' + T.id + '/' + E.teams.home.id,
			name: E.teams.home.name,
			uid: E.teams.home.uid
		},
		away: {
			id: !!E.teams.away ? '/team/' + T.id + '/' + E.teams.away.id : null,
			name: !!E.teams.away ? E.teams.away.name : null,
			uid: !!E.teams.away ? E.teams.away.uid : null
		}
	}
})

const normalizeMenu = (menu: any): Record<string, LiveSportNode> => {
	const sports = menu.sports;
	const categories = menu.categories;
	const tournaments = menu.tournaments;
	const events = Object.values(menu.events) as any[];

	const menuTree: Record<string, LiveSportNode> = {};

	for (var event of events) {
		const tournament = tournaments[event.tournament];
		const category = categories[tournament.category];
		const sport = sports[category.sport];

		if (!menuTree['/sport/' + sport.id]) {
			menuTree['/sport/' + sport.id] = {
				id: '/sport/' + sport.id,
				name: sport.name,
				label: sport.label,
				count: 0,
				order: sport.order,
				sub: {},
			};
		}
		if (!menuTree['/sport/' + sport.id].sub['/tournament/' + sport.id + '/' + tournament.id]) {
			menuTree['/sport/' + sport.id].sub['/tournament/' + sport.id + '/' + tournament.id] = {
				parent: '/sport/' + sport.id,
				id: '/tournament/' + sport.id + '/' + tournament.id,
				name: category.name + ' - ' + tournament.name,
				label: category.label + ' - ' + tournament.label,
				order: tournament.order,
				elite: !!PrematchFeedService.mainTournaments['/tournament/' + sport.id + '/' + category.id + '/' + tournament.id],
				sub: {},
				category: category,
			};
		}
		menuTree['/sport/' + sport.id]
			.sub['/tournament/' + sport.id + '/' + tournament.id]
			.sub['/event/' + event.id] = normalizeEvent(sport, tournament, event);

		menuTree['/sport/' + sport.id].count += 1;
	}

	return menuTree;
}

interface CustomWebSocket extends WebSocket {
	rsend: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => void;
}

export default class Live extends Component {
	private static livestreamsEventList: number[] = [];
	private static availableLivestreamInterval: any = null;
	private static ws: CustomWebSocket | false = false;
	private static queue: string[] = [];
	private static topics: Record<string, any> = {};
	private static listeners: Array<() => void> = [];
	private static lastrawmessage: string = '';
	
	// static menu: Record<string, LiveSportNode> | false = false;
	// static events: Record<string, LiveEventNode> = {};

	private static _menuBox = makeReactive({ value: false as Record<string, LiveSportNode> | false });
	private static _eventsBox = makeReactive({ value: {} as Record<string, LiveEventNode> });

	static get menu() { return Live._menuBox.value; }
	static set menu(val) { Live._menuBox.value = val; }

	static get events() { return Live._eventsBox.value; }
	static set events(val) { Live._eventsBox.value = val; }

	componentWillMount() {
		Live.connect();
	}

	render() {
		return null;
	}

	componentWillUnmount() {
		if (!!Live.availableLivestreamInterval) {
			clearInterval(Live.availableLivestreamInterval);
			Live.availableLivestreamInterval = null;
		}
	}

	static connect() {
		Live.ws && Live.ws.close();
		const ws = new WebSocket(Config.softionLiveAPI + '?lang=es') as CustomWebSocket; 
		Live.ws = ws;
		Live.ws.rsend = ws.send;
		Live.ws.send = (data: string) => Live.ws && Live.ws.readyState == WebSocket.OPEN ? Live.ws.rsend(data) : Live.queue.push(data);
		Live.ws.onopen = () => {
			Live.queue.splice(0).forEach(m => Live.ws && Live.ws.rsend(m));
		};

		// usare .hostname invece di .host se hai un dominio con la porta e non la vuoi qui
		Live.ws.send(JSON.stringify({ "type": "login", "data": { "lang": Config.lang, "skin": new URL(Config.softionAPI).host } }));
		Live.ws.send(JSON.stringify({ "type": "menu" }));

		Live.ws.onmessage = (msg) => { Live.lastrawmessage = msg.data; return Live.handleMessage(JSON.parse(msg.data)); };
		Live.ws.onerror = Live.ws.onclose = once((e: any) => setTimeout(Live.connect, 5e3));//reconnect

		this.getAvailableLiveStreams();
		Live.availableLivestreamInterval = setInterval(() => this.getAvailableLiveStreams(), 30e3);
	}

	static getAvailableLiveStreams() {
		$.getJSON(
			Config.softionAPI + '/livestreams/getEventsWithLiveStream' + (!!Config.publickey ? '?public=' + Config.publickey : ''),
			(something: any, data: any) => {
				if (!!data && !!data.events) {
					this.livestreamsEventList = data.events;
				}
			}
		);
	}

	static getLiveStreamPlayer(event: { id: string }, callback: (player: string | false) => void) {
		$.getJSON(
			Config.softionAPI + '/livestreams/getLiveStreamPlayer/' + event.id.replace('/event/', '') + (!!Config.publickey ? '?public=' + Config.publickey : ''),
			(something: any, data: any) => {
				if (!!data && !!data.livestream) {
					if (!!callback) {
						callback(data.livestream);
						return;
					}
				}

				callback(false);
			}
		);
	}

	static hasLiveStream(event: { id: string }) {
		//console.info(event.id.replace('/event/',''), this.livestreamsEventList, this.livestreamsEventList.indexOf(parseInt(event.id.replace('/event/'))));
		return this.livestreamsEventList.indexOf(parseInt(event.id.replace('/event/', ''))) !== -1;
	}

	static destroy() {
		if (Live.ws) {
			Live.ws.onerror = Live.ws.onclose = null;
			Live.ws.close();
		}
	}

	static everyEvent(menu: any) {//flatten menu into single "events" object
		return (Object.values(menu.events) as any[]).filter(E => !!Config.allowedLiveSports["/sport/" + E.sport]).reduce(groupBy('id'), {});
	}

	static getSportFromOriginalEvent(event: any, menu: any) {
		return menu.sports[event.sport];
	}

	static getTournamentFromOriginalEvent(event: any, menu: any) {
		return menu.tournaments[event.tournament || event.parent.id];
	}

	static buildEventsDB(topics: Record<string, any>) {
		return Object.values({
			...Live.everyEvent(topics.menu),
			...Object.keys(topics).filter(k => k != 'menu' && !!topics[k] && !!topics[k].current).map(k => topics[k]).reduce(groupBy('id'), {})
		})
			.filter((E: any) => !!E && !!E.id && !!Live.getSportFromOriginalEvent(E, topics.menu) && !!Live.getTournamentFromOriginalEvent(E, topics.menu))
			.map(E => normalizeEvent(
				Live.getSportFromOriginalEvent(E, topics.menu),
				Live.getTournamentFromOriginalEvent(E, topics.menu),
				E
			)).reduce(groupBy('id'), {});
	}

	static handleMessage(data: any) {
		switch (data.type) {
			case 'rebase'://uno solo per volta.
				Live.topics[data.topic] = data.data;
				for (let topicName in Live.topics) {
					if (topicName != 'menu' && topicName != data.topic)
						delete Live.topics[topicName]
				}
				break;
			case 'update':
				Live.topics[data.topic] = applyPatch(Live.topics[data.topic], data.data as Operation[]).newDocument;
				break;
			case 'closed':
				delete Live.topics[data.topic];
				Live.setTopic('menu')
				break;
		}

		//> Normalizzazione menu applicando sport blacklist e tournament blacklist
		// console.info(Live.topics.menu);
		Live.menu = Object.entries(normalizeMenu(Live.topics.menu)).reduce((obj: Record<string, LiveSportNode>, [key, item]) => {
			if (LIVE_SPORT_BLACKLIST.indexOf(key) === -1) {
				let sport = { ...item };
				sport.count = 0;

				if (!Config.allowedLiveSports[sport.id]) {
					return obj;
				}

				if (!!sport.sub) {
					sport.sub = Object.entries(sport.sub).reduce((subobj: Record<string, LiveTournamentNode>, [subkey, subdata]) => {
						if (
							subdata.name.toLowerCase().indexOf('electr\u00f3nicas') === -1
							&& subdata.name.toLowerCase().indexOf('simulated') === -1
							&& subdata.name.toLowerCase().indexOf('simulada') === -1
							&& subdata.name.toLowerCase().indexOf('simulado') === -1
						) {
							subobj[subkey] = subdata;
							sport.count += Object.keys(subdata.sub).length;
						}
						return subobj;
					}, {});
				}

				if (sport.count > 0) {
					obj[key] = sport;
				}
			}
			return obj;
		}, {});

		Live.events = Live.buildEventsDB(Live.topics);
		Live.listeners.forEach(cb => cb());
	}

	static getTournament(path: string) {
		const [sid, tid] = path.split('/').slice(2);
		return Live.menu && Live.menu['/sport/' + sid].sub['/tournament/' + sid + '/' + tid];
	}
	static getOdd(path: string) {
		const [eid, mid, oid] = path.split('/').slice(2);
		const market = Live.getMarket(path.split('/').slice(0, -1).join('/'));
		return market && market.sub['/odd/' + eid + '/' + mid + '/' + oid];
	}
	static getMarket(path: string) {
		const [eid, mid] = path.split('/').slice(2);
		const event = Live.getEvent(path.split('/').slice(0, -1).join('/'));
		return event && event.sub['/market/' + eid + '/' + mid];
	}
	static getEvent(path: string) {
		const [eid] = path.split('/').slice(2);
		return Live.events['/event/' + eid];
	}

	static onUpdate(cb: () => void) {
		Live.listeners = (Live.listeners || []).concat(cb);
	}
	static stopUpdate(cb: () => void) {
		Live.listeners = (Live.listeners || []).filter(c => c != cb);
	}

	static setTopic(topic: string) {
		const msg = topic == 'menu' ? { "type": "menu" } : { "type": "event", "eventid": topic };
		if (Live.ws)
			Live.ws.send(JSON.stringify(msg));
		else
			Live.queue.push(JSON.stringify(msg));
	}
}

(window as any).stopLive = Live.destroy;


const LIVE_SPORT_BLACKLIST: string[] = [
];

const TABS: Tab[] = [
	{ id: 1, name: 'Main' },
	{ id: 2, name: 'Goals' },
	{ id: 3, name: 'Handicap' },
	{ id: 4, name: 'Correct Score' },
	{ id: 5, name: '1st Half' },
	{ id: 6, name: '2nd Half' },
	{ id: 7, name: 'Home' },
	{ id: 8, name: 'Away' },
	{ id: 9, name: 'Asian' },
	{ id: 10, name: 'Points Handicap' },
	{ id: 11, name: 'Total Points' },
	{ id: 12, name: 'Set Betting' },
];

const marketCategories = (rawMarket: any): Tab[] => {
	const { id, label } = rawMarket;
	const result: Tab[] = [];
	if (label.trim().match(/^(1x2[^ ]?|Double Chance|Total|Home Team Over\/Under|Away Team Over\/Under|\d+(st|nd|rd|th) Goal|Double Chance & GG\/NG|GG\/NG|Both teams to score|1st Half Total|1st Half - 1X2|Corners)/ig)) {
		result.push(TABS[0]);
	}
	if (label.match(/(Total Goals)|(Total \()/ig)) {
		result.push(TABS[1]);
	}
	if (label.match(/(Handicap)/ig)) {
		result.push(TABS[2]);
	}
	if (label == 'Correct Score') {
		result.push(TABS[3]);
	}
	if (label.indexOf('1st Half') >= 0) {
		result.push(TABS[4]);
	}
	if (label.indexOf('2nd Half') >= 0) {
		result.push(TABS[5]);
	}
	if (label.match(/(home|team 1)/ig)) {
		result.push(TABS[6]);
	}
	if (label.match(/(away|team 2)/ig)) {
		result.push(TABS[7]);
	}
	if (label.indexOf('Asian') >= 0) {
		result.push(TABS[8]);
	}
	if (label == 'Points Handicap') {
		result.push(TABS[9]);
	}
	if (label == 'Total Points') {
		result.push(TABS[10]);
	}
	if (label == 'Set Betting') {
		result.push(TABS[11]);
	}
	return result;
}


